'use client';
import React, { useEffect, useRef, useState } from 'react';

interface TableRow {
  id: string;
  presentation: string;
  presentationDetail: string;
  pathway: string;
  pathwayDetail: string;
  resource: string;
  resourceType: 'PDF' | 'Algorithm' | 'Case Study' | 'Reference Card';
  expandContent: {
    steps: string[];
    keyTests: string[];
    clinicalPearl: string;
  };
}

interface ComparisonTable {
  id: string;
  badge: string;
  title: string;
  subtitle: string;
  rows: TableRow[];
}

const tables: ComparisonTable[] = [
  {
    id: 'anemia',
    badge: 'Red Cell Disorders',
    title: 'Unexplained Anemia',
    subtitle: 'When Hgb drops and the cause stays hidden',
    rows: [
      {
        id: 'a1',
        presentation: 'Normocytic anemia, normal reticulocyte count',
        presentationDetail: 'Hgb 9.2 g/dL · MCV 88 fL · Retic 1.1%',
        pathway: 'Bone marrow reserve + erythropoietin axis evaluation',
        pathwayDetail: 'EPO level · Serum hepcidin · Soluble transferrin receptor',
        resource: 'Normocytic Anemia Decision Tree',
        resourceType: 'Algorithm',
        expandContent: {
          steps: [
            'Obtain EPO level — if inappropriately low, evaluate for CKD or endocrine suppression',
            'Serum hepcidin distinguishes ACD from true iron deficiency (hepcidin elevated in ACD)',
            'If hepcidin normal/low: evaluate for early iron deficiency with sTfR/ferritin index',
            'Bone marrow biopsy if cytopenias persist >6 weeks without clear etiology',
          ],
          keyTests: ['EPO level', 'Serum hepcidin', 'Soluble TfR', 'Ferritin + CRP', 'Peripheral smear review'],
          clinicalPearl: 'Hepcidin >25 ng/mL in the setting of anemia virtually excludes true iron deficiency and points toward inflammatory suppression of erythropoiesis.',
        },
      },
      {
        id: 'a2',
        presentation: 'Microcytic anemia, ferritin 180 ng/mL',
        presentationDetail: 'Hgb 8.7 g/dL · MCV 72 fL · Ferritin 180 · TIBC low',
        pathway: 'Thalassemia vs. ACD vs. Sideroblastic differentiation',
        pathwayDetail: 'Hemoglobin electrophoresis · Zinc protoporphyrin · Bone marrow iron stain',
        resource: 'Microcytic Anemia with Elevated Ferritin Workup',
        resourceType: 'PDF',
        expandContent: {
          steps: [
            'Elevated ferritin with microcytosis rules out simple IDA — consider thalassemia trait first',
            'Hemoglobin electrophoresis: HbA2 >3.5% confirms beta-thalassemia trait',
            'Zinc protoporphyrin elevated in lead poisoning and sideroblastic anemia',
            'Bone marrow with ring sideroblasts (>15% of erythroblasts) = sideroblastic anemia',
          ],
          keyTests: ['Hemoglobin electrophoresis', 'Zinc protoporphyrin', 'Serum lead', 'Bone marrow iron stain', 'Reticulocyte hemoglobin'],
          clinicalPearl: 'A ferritin >100 with microcytosis in a patient of Mediterranean or Southeast Asian descent should prompt Hgb electrophoresis before any iron supplementation.',
        },
      },
      {
        id: 'a3',
        presentation: 'Macrocytic anemia, B12 borderline normal',
        presentationDetail: 'Hgb 10.1 g/dL · MCV 104 fL · B12 248 pg/mL',
        pathway: 'Functional B12 deficiency assessment',
        pathwayDetail: 'Methylmalonic acid · Homocysteine · Anti-IF antibodies',
        resource: 'B12 Borderline Workup Reference Card',
        resourceType: 'Reference Card',
        expandContent: {
          steps: [
            'Serum B12 200–400 pg/mL is a diagnostic gray zone — MMA and homocysteine required',
            'Elevated MMA (>271 nmol/L) confirms functional B12 deficiency even with "normal" serum level',
            'Anti-intrinsic factor antibodies: 50–70% sensitivity but >95% specific for pernicious anemia',
            'Trial of B12 replacement with reticulocyte response at day 7 is both diagnostic and therapeutic',
          ],
          keyTests: ['Methylmalonic acid', 'Homocysteine', 'Anti-IF antibody', 'Anti-parietal cell Ab', 'Reticulocyte count'],
          clinicalPearl: 'Methylmalonic acid is the most sensitive marker of intracellular B12 deficiency. A normal MMA in macrocytic anemia redirects the workup to folate, liver disease, or hypothyroidism.',
        },
      },
    ],
  },
  {
    id: 'coagulation',
    badge: 'Coagulation',
    title: 'Recurrent Thrombosis & Unexplained Bleeding',
    subtitle: 'Beyond the standard coag panel',
    rows: [
      {
        id: 'c1',
        presentation: 'Recurrent DVT, age 34, no provocation',
        presentationDetail: '2nd unprovoked DVT · Family history of clotting · On OCP',
        pathway: 'Comprehensive thrombophilia workup with timing protocol',
        pathwayDetail: 'Factor V Leiden · Prothrombin G20210A · Protein C/S/Antithrombin · Antiphospholipid panel',
        resource: 'Thrombophilia Workup Timing Guide',
        resourceType: 'PDF',
        expandContent: {
          steps: [
            'Do NOT test within 3 months of acute thrombosis — acute phase reactants falsely elevate Protein C and S',
            'Warfarin reduces Protein C and S — hold 2 weeks before testing; DOAC affects functional assays',
            'Antiphospholipid syndrome requires 2 positive tests ≥12 weeks apart (lupus anticoagulant, anticardiolipin, anti-β2GP1)',
            'Factor V Leiden R506Q and Prothrombin G20210A by PCR — not affected by anticoagulation timing',
          ],
          keyTests: ['Factor V Leiden (PCR)', 'PT G20210A (PCR)', 'Protein C activity', 'Protein S free antigen', 'Antithrombin activity', 'Lupus anticoagulant screen', 'Anticardiolipin IgG/IgM', 'Anti-β2GP1'],
          clinicalPearl: 'Lupus anticoagulant testing is invalid while on any anticoagulant. Plan testing windows carefully — the most common error in thrombophilia workup is incorrect timing.',
        },
      },
      {
        id: 'c2',
        presentation: 'Easy bruising, prolonged bleeding, normal PT/PTT',
        presentationDetail: 'Mucocutaneous bleeding · Normal PT 11s · Normal PTT 30s · Normal CBC',
        pathway: 'Platelet function and von Willebrand disease evaluation',
        pathwayDetail: 'PFA-100 · VWF antigen · VWF activity (ristocetin cofactor) · VWF multimers',
        resource: 'VWD Subtype Classification Algorithm',
        resourceType: 'Algorithm',
        expandContent: {
          steps: [
            'PFA-100 closure time: sensitive screening for VWD and platelet function defects (not affected by aspirin in VWD)',
            'VWF:Ag and VWF:RCo ratio <0.6 suggests VWD type 2 (qualitative defect)',
            'VWF multimer analysis distinguishes type 2A (absent high/intermediate multimers) from 2B and 2M',
            'Platelet aggregation studies (ADP, collagen, epinephrine, ristocetin) if VWD excluded',
          ],
          keyTests: ['PFA-100 (collagen/ADP)', 'VWF antigen', 'VWF ristocetin cofactor', 'VWF multimers', 'Factor VIII activity', 'Platelet aggregation'],
          clinicalPearl: 'VWF levels are blood group dependent — type O individuals have VWF levels 25% lower than type A. Always interpret VWF antigen relative to ABO blood group before diagnosing type 1 VWD.',
        },
      },
    ],
  },
  {
    id: 'morphology',
    badge: 'Smear & Morphology',
    title: 'Abnormal Peripheral Smear',
    subtitle: 'Findings that demand a second look',
    rows: [
      {
        id: 'm1',
        presentation: 'Circulating blasts, WBC 28,000, no prior history',
        presentationDetail: '12% blasts on smear · WBC 28k · Hgb 9.1 · Plt 48k',
        pathway: 'Acute leukemia vs. accelerated phase MPN workup',
        pathwayDetail: 'Flow cytometry immunophenotyping · Bone marrow biopsy · Cytogenetics + NGS panel',
        resource: 'Blast Identification & Triage Protocol',
        resourceType: 'Algorithm',
        expandContent: {
          steps: [
            'Flow cytometry on peripheral blood within 24 hours — do not wait for bone marrow',
            'CD34+, CD117+, MPO+ → AML lineage; TdT+, CD19+, CD10+ → B-ALL',
            'Bone marrow aspirate and biopsy for morphology, cytogenetics (FISH + karyotype), and NGS',
            'NGS 54-gene myeloid panel: FLT3-ITD, NPM1, IDH1/2, RUNX1, TP53 — critical for prognosis and targeted therapy',
          ],
          keyTests: ['Peripheral blood flow cytometry', 'Bone marrow aspirate + biopsy', 'Karyotype + FISH', 'NGS myeloid panel', 'LDH · Uric acid · Phosphorus (TLS risk)'],
          clinicalPearl: 'FLT3-ITD status must be known before induction in AML — midostaurin is added to 7+3 in FLT3-mutated AML. This result changes day-1 treatment decisions.',
        },
      },
      {
        id: 'm2',
        presentation: 'Teardrop cells + nucleated RBCs + leukoerythroblastic picture',
        presentationDetail: 'Dacrocytes · nRBCs · Left shift · Splenomegaly on exam',
        pathway: 'Myelofibrosis vs. bone marrow infiltrative process',
        pathwayDetail: 'JAK2 V617F · CALR · MPL mutations · Bone marrow trephine biopsy',
        resource: 'Leukoerythroblastic Reaction Workup',
        resourceType: 'PDF',
        expandContent: {
          steps: [
            'Leukoerythroblastic picture = bone marrow stress — always requires bone marrow evaluation',
            'JAK2 V617F positive in ~95% of PV, ~55% of ET and PMF',
            'CALR exon 9 mutation in ~25% of JAK2-negative ET and PMF',
            'Bone marrow trephine: reticulin/collagen fibrosis grading (MF-0 to MF-3) defines disease stage',
          ],
          keyTests: ['JAK2 V617F (allele burden %)', 'CALR exon 9', 'MPL W515L/K', 'Bone marrow trephine + reticulin', 'LDH · Uric acid', 'Erythropoietin level'],
          clinicalPearl: 'JAK2 V617F allele burden >50% correlates with bone marrow fibrosis and splenomegaly severity. Serial monitoring of allele burden can track disease progression or response to ruxolitinib.',
        },
      },
    ],
  },
  {
    id: 'iron',
    badge: 'Iron Metabolism',
    title: 'Elevated Ferritin — The Diagnostic Trap',
    subtitle: 'When the number is high but the story is complex',
    rows: [
      {
        id: 'i1',
        presentation: 'Ferritin 1,800 ng/mL, transferrin saturation 52%',
        presentationDetail: 'Male · Age 42 · Fatigue · Elevated LFTs · Bronze skin',
        pathway: 'Hereditary hemochromatosis evaluation and organ staging',
        pathwayDetail: 'HFE gene mutation (C282Y/H63D) · Liver MRI (R2* iron quantification) · Hepatology referral threshold',
        resource: 'Hemochromatosis Staging & Management Guide',
        resourceType: 'PDF',
        expandContent: {
          steps: [
            'HFE C282Y homozygosity + TSAT >45% confirms HH — no further genetic testing needed',
            'Liver MRI with R2* sequence quantifies hepatic iron concentration non-invasively',
            'Liver biopsy indicated if: ferritin >1000 + abnormal LFTs + age >40 (fibrosis risk)',
            'Phlebotomy goal: ferritin <50 ng/mL — typically requires weekly phlebotomy for 1–2 years',
          ],
          keyTests: ['HFE C282Y/H63D genotype', 'Transferrin saturation', 'Liver MRI R2*', 'LFTs + GGT', 'Fasting glucose (bronze diabetes)', 'Echocardiogram (cardiac iron)'],
          clinicalPearl: 'Transferrin saturation >45% on a fasting morning specimen is the most sensitive screening test for HH. Ferritin alone is insufficient — it rises late and is confounded by inflammation.',
        },
      },
      {
        id: 'i2',
        presentation: 'Ferritin 3,400 ng/mL, normal TSAT, fever, cytopenias',
        presentationDetail: 'Ferritin 3,400 · TSAT 28% · WBC 2.1k · Plt 55k · Splenomegaly',
        pathway: 'Hemophagocytic lymphohistiocytosis evaluation',
        pathwayDetail: 'HScore calculation · Bone marrow biopsy · NK cell activity · sCD25 · Triglycerides · Fibrinogen',
        resource: 'HLH HScore Calculator & Diagnostic Criteria',
        resourceType: 'Algorithm',
        expandContent: {
          steps: [
            'HScore ≥169 has 93% sensitivity and 86% specificity for HLH',
            'Bone marrow biopsy: hemophagocytosis (macrophages engulfing RBCs, WBCs, platelets)',
            'sCD25 (soluble IL-2 receptor) >2400 U/mL supports HLH diagnosis',
            'NK cell cytotoxicity <10% of normal: functional assay required, not just NK count',
          ],
          keyTests: ['Ferritin (serial)', 'Triglycerides', 'Fibrinogen', 'sCD25 (IL-2R)', 'NK cell cytotoxicity', 'Bone marrow biopsy', 'HScore calculation'],
          clinicalPearl: 'Ferritin >10,000 ng/mL has a 90% positive predictive value for HLH in the right clinical context. The diagnostic mistake is attributing extreme hyperferritinemia to liver disease alone without completing the HScore.',
        },
      },
    ],
  },
];

export default function DiagnosticsSection() {
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  const toggleRow = (id: string) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const resourceBadgeColor = (type: TableRow['resourceType']) => {
    const map: Record<string, string> = {
      PDF: 'badge-rose',
      Algorithm: 'badge-sky',
      'Case Study': 'badge-sky',
      'Reference Card': 'badge-sky',
    };
    return map[type] || 'badge-sky';
  };

  return (
    <section id="diagnostics" ref={sectionRef} className="py-24 px-6" style={{ background: '#F4F7FA' }}>
      <div className="max-w-6xl mx-auto">

        {/* Section Header */}
        <div className="reveal mb-20 text-center">
          <span
            className="text-xs font-bold tracking-widest uppercase block mb-4"
            style={{ color: '#89B0D0' }}
          >
            Diagnostic Pathways
          </span>
          <h2
            className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight mb-5"
            style={{ color: '#3B4856' }}
          >
            The Presentation{' '}
            <span className="italic font-light" style={{ color: '#89B0D0' }}>
              vs.
            </span>{' '}
            The Workup
          </h2>
          <p
            className="text-lg font-light max-w-xl mx-auto leading-relaxed"
            style={{ color: '#5A6B7C' }}
          >
            Each row is a clinical mystery. Click to reveal the structured
            diagnostic pathway — and the downloadable resource behind it.
          </p>
        </div>

        {/* Tables */}
        <div className="space-y-16">
          {tables.map((table, tIdx) => (
            <div
              key={table.id}
              className={`reveal reveal-delay-${Math.min(tIdx + 1, 4)}`}
            >
              {/* Table Header */}
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <div>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide mb-3 ${resourceBadgeColor(table.rows[0].resourceType)}`}
                  >
                    {table.badge}
                  </span>
                  <h3
                    className="font-serif text-2xl md:text-3xl font-semibold"
                    style={{ color: '#3B4856' }}
                  >
                    {table.title}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: '#8A9AAA' }}>
                    {table.subtitle}
                  </p>
                </div>
                <a
                  href="#resources"
                  className="btn-rose inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap self-start md:self-auto"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download Workup Guide
                </a>
              </div>

              {/* Table */}
              <div
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(137,176,208,0.25)' }}
              >
                {/* Column headers */}
                <div
                  className="grid grid-cols-[1fr_1fr_auto] gap-0 px-6 py-3 text-xs font-bold tracking-widest uppercase"
                  style={{
                    background: '#3B4856',
                    color: 'rgba(244,247,250,0.6)',
                  }}
                >
                  <span>Clinical Presentation</span>
                  <span>Diagnostic Pathway</span>
                  <span className="text-right">Resource</span>
                </div>

                {/* Rows */}
                {table.rows.map((row, rIdx) => (
                  <div
                    key={row.id}
                    style={{
                      borderTop: rIdx > 0 ? '1px solid rgba(137,176,208,0.18)' : undefined,
                      background: '#F4F7FA',
                    }}
                  >
                    {/* Main row */}
                    <div
                      className="table-row-expandable grid grid-cols-[1fr_1fr_auto] gap-0 px-6 py-5 items-start"
                      onClick={() => toggleRow(row.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && toggleRow(row.id)}
                      aria-expanded={openRows[row.id]}
                    >
                      {/* Presentation */}
                      <div className="pr-6">
                        <p className="text-sm font-semibold mb-1" style={{ color: '#3B4856' }}>
                          {row.presentation}
                        </p>
                        <p className="text-xs font-mono" style={{ color: '#8A9AAA' }}>
                          {row.presentationDetail}
                        </p>
                      </div>
                      {/* Pathway */}
                      <div className="pr-6">
                        <p className="text-sm font-medium mb-1" style={{ color: '#3B4856' }}>
                          {row.pathway}
                        </p>
                        <p className="text-xs" style={{ color: '#8A9AAA' }}>
                          {row.pathwayDetail}
                        </p>
                      </div>
                      {/* Resource + toggle */}
                      <div className="flex items-center gap-3 min-w-[160px] justify-end">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${resourceBadgeColor(row.resourceType)}`}>
                          {row.resourceType}
                        </span>
                        <div
                          className={`expand-icon w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${openRows[row.id] ? 'open' : ''}`}
                          style={{
                            background: openRows[row.id] ? 'rgba(196,91,106,0.12)' : 'rgba(137,176,208,0.12)',
                            color: openRows[row.id] ? '#C45B6A' : '#89B0D0',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Expanded content */}
                    <div className={`expand-content ${openRows[row.id] ? 'open' : ''}`}>
                      <div
                        className="px-6 pb-6 pt-2"
                        style={{ borderTop: '1px solid rgba(137,176,208,0.18)', background: '#EAF2F8' }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
                          {/* Steps */}
                          <div className="md:col-span-2">
                            <p
                              className="text-xs font-bold tracking-widest uppercase mb-3"
                              style={{ color: '#89B0D0' }}
                            >
                              Diagnostic Steps
                            </p>
                            <ol className="space-y-2">
                              {row.expandContent.steps.map((step, i) => (
                                <li key={i} className="flex gap-3 text-sm" style={{ color: '#3B4856' }}>
                                  <span
                                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                                    style={{ background: '#89B0D0', color: '#F4F7FA' }}
                                  >
                                    {i + 1}
                                  </span>
                                  <span className="leading-relaxed font-light">{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>

                          {/* Key tests + pearl */}
                          <div className="space-y-5">
                            <div>
                              <p
                                className="text-xs font-bold tracking-widest uppercase mb-2"
                                style={{ color: '#89B0D0' }}
                              >
                                Key Tests
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {row.expandContent.keyTests.map((test) => (
                                  <span
                                    key={test}
                                    className="px-2.5 py-1 rounded-full text-xs font-medium badge-sky"
                                  >
                                    {test}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div
                              className="p-4 rounded-xl"
                              style={{
                                background: 'rgba(196,91,106,0.07)',
                                border: '1px solid rgba(196,91,106,0.2)',
                              }}
                            >
                              <p
                                className="text-xs font-bold tracking-widest uppercase mb-2"
                                style={{ color: '#C45B6A' }}
                              >
                                Clinical Pearl
                              </p>
                              <p className="text-xs leading-relaxed font-light" style={{ color: '#3B4856' }}>
                                {row.expandContent.clinicalPearl}
                              </p>
                            </div>
                            <a
                              href="#resources"
                              className="btn-rose w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              Download: {row.resource}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}